import {FieldError, FieldErrors, FieldValues, Path} from 'react-hook-form'

const arrayIndexDotSegmentPattern = /\.(\d+)(?=\.|$)/g
const arrayIndexBracketSegmentPattern = /\[(\d+)\]/g

const isFieldError = (value: unknown): value is FieldError => {
  return Boolean(value) && typeof value === 'object' && 'message' in (value as object)
}

const getValueAtPath = (source: unknown, path: string): unknown => {
  const normalized = path.replace(arrayIndexBracketSegmentPattern, '.$1')
  const segments = normalized.split('.').filter(Boolean)

  let current: unknown = source
  for (const segment of segments) {
    if (!current || typeof current !== 'object') {
      return undefined
    }

    current = (current as Record<string, unknown>)[segment]
  }

  return current
}

export const getFieldError = <T extends FieldValues>(
  errors: FieldErrors<T> | undefined,
  name: Path<T>
): FieldError | undefined => {
  if (!errors) {
    return undefined
  }

  const key = String(name)
  const errorRecord = errors as unknown as Record<string, unknown>

  const directError = errorRecord[key]
  if (isFieldError(directError)) {
    return directError
  }

  const bracketKey = key.replace(arrayIndexDotSegmentPattern, '[$1]')
  if (bracketKey !== key) {
    const bracketDirectError = errorRecord[bracketKey]
    if (isFieldError(bracketDirectError)) {
      return bracketDirectError
    }
  }

  const nestedError = getValueAtPath(errors, key)
  if (isFieldError(nestedError)) {
    return nestedError
  }

  if (bracketKey !== key) {
    const nestedBracketError = getValueAtPath(errors, bracketKey)
    if (isFieldError(nestedBracketError)) {
      return nestedBracketError
    }
  }

  return undefined
}
